class StockfishEngine {
  constructor() {
    this.worker = null;
    this.isReady = false;
    this.callbacks = {
      ready: [],
      message: [],
      error: [],
    };
    this.lastPosition = null;
    this.currentAnalysis = {
      bestEvaluation: null,
      principalVariations: [],
      analysisInProgress: false
    };
  }

  // Initialize the engine
  init() {
    if (this.worker) {
      return Promise.resolve();
    }


    return new Promise((resolve, reject) => {
      try {
        // Define possible paths for the Stockfish worker
        const possibleWorkerPaths = [
          '/stockfish-nnue-16-single.js',      // Root path
          '/stockfish.js',                      // Alternative stockfish file
          '/stockfish.worker.js'                // Worker specific file
        ];

        let pathIndex = 0;
        const tryNextWorker = () => {
          if (pathIndex >= possibleWorkerPaths.length) {
            reject(new Error(`Failed to initialize Stockfish worker from any path. Tried: ${possibleWorkerPaths.join(', ')}`));
            return;
          }

          const workerPath = possibleWorkerPaths[pathIndex];

          try {
            // Create a web worker for the Stockfish engine
            const worker = new Worker(workerPath);

            // Set up message handling
            worker.onmessage = (event) => {
              const message = event.data;

              // Check if it's a custom message or standard output
              if (typeof message === 'string') {

                // Notify all message listeners
                this.callbacks.message.forEach(callback => callback(message));

                // Check for readyok
                if (message.includes('readyok')) {
                  this.isReady = true;
                  this.callbacks.ready.forEach(callback => callback());
                }
              } else if (message.cmd === 'custom') {
                // Handle any custom messages from the engine
                // console.log('Stockfish custom message:', message.userData);
              }
            };

            // Handle worker errors
            worker.onerror = (error) => {
              console.error('Stockfish worker error:', error);

              // Try the next worker if this one fails
              pathIndex++;
              tryNextWorker();
            };

            // Save the worker
            this.worker = worker;

            // Initialize UCI mode
            this.sendCommand('uci');
            this.waitForReady()
              .then(() => {
                resolve();
              })
              .catch(error => {
                console.error('Error initializing Stockfish:', error);
                this.worker.terminate();
                this.worker = null;

                // Try the next worker if initialization fails
                pathIndex++;
                tryNextWorker();
              });
          } catch (error) {
            console.error(`Error creating worker from ${workerPath}:`, error);

            // Try the next worker
            pathIndex++;
            tryNextWorker();
          }
        };

        // Start trying workers
        tryNextWorker();
      } catch (error) {
        console.error('Error initializing Stockfish engine:', error);
        reject(error);
      }
    });
  }

  // Wait for the engine to be ready
  waitForReady() {
    if (this.isReady) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      // Set a timeout to prevent waiting forever
      const timeout = setTimeout(() => {
        reject(new Error('Stockfish engine initialization timed out'));
      }, 5000);

      // Add a callback to be executed when the engine is ready
      this.callbacks.ready.push(() => {
        clearTimeout(timeout);
        resolve();
      });

      // Send isready command to the engine
      this.sendCommand('isready');
    });
  }

  // Send a command to the engine
  sendCommand(command) {
    try {
      if (!this.worker) {
        console.error('Engine not initialized');
        return;
      }

      this.worker.postMessage(command);
    } catch (error) {
      console.error('Error sending command to Stockfish:', error);
    }
  }

  // Analyze a position
  analyzePosition(fen, options = {}) {
    const { depth = 15, multipv = 3 } = options;
    this.lastPosition = fen;

    // Reset current analysis data
    this.currentAnalysis = {
      bestEvaluation: null,
      principalVariations: [],
      analysisInProgress: true
    };

    // Determine who's turn it is from the FEN string
    const fenParts = fen.split(' ');
    const sideToMove = fenParts[1]; // 'w' for White, 'b' for Black
    const isBlackToMove = sideToMove === 'b';

    // Store this info to normalize evaluations later
    this.currentAnalysis.isBlackToMove = isBlackToMove;

    // Make sure engine is initialized
    return this.init()
      .then(() => this.waitForReady())
      .then(() => {
        // Set options
        this.sendCommand(`setoption name MultiPV value ${multipv}`);

        // Set position
        this.sendCommand(`position fen ${fen}`);

        // Start analysis
        this.sendCommand(`go depth ${depth}`);

        // Set up listener for normalizing evaluations
        const normalizeEvaluationListener = this.onMessage(message => {
          // Process engine output to normalize scores
          if (message.includes('info depth') && (message.includes('score cp') || message.includes('score mate'))) {
            try {
              // Extract score information
              let evaluation;

              if (message.includes('score cp')) {
                const cpMatch = message.match(/score cp (-?\d+)/);
                if (cpMatch) {
                  let cpScore = parseInt(cpMatch[1]) / 100; // Convert centipawns to pawns

                  // If it's Black's turn, negate the score to get White's perspective
                  if (isBlackToMove) {
                    cpScore = -cpScore;
                  }

                  evaluation = cpScore;

                  // Update best evaluation if this is the top line
                  if (message.includes('multipv 1')) {
                    this.currentAnalysis.bestEvaluation = evaluation;
                  }
                }
              } else if (message.includes('score mate')) {
                const mateMatch = message.match(/score mate (-?\d+)/);
                if (mateMatch) {
                  let mateIn = parseInt(mateMatch[1]);

                  // If it's Black's turn, negate the mate score
                  if (isBlackToMove) {
                    mateIn = -mateIn;
                  }

                  // Format as M+n for mate by White, M-n for mate by Black
                  evaluation = mateIn > 0 ? `M${Math.abs(mateIn)}` : `M-${Math.abs(mateIn)}`;

                  // Update best evaluation if this is the top line
                  if (message.includes('multipv 1')) {
                    this.currentAnalysis.bestEvaluation = evaluation;
                  }
                }
              }
            } catch (error) {
              console.error('Error processing engine output:', error);
            }
          }
        });

        // Store the listener to remove it when analysis is stopped
        this.currentAnalysis.evaluationListener = normalizeEvaluationListener;
      })
      .catch(error => {
        this.currentAnalysis.analysisInProgress = false;
        throw error;
      });
  }

  // Get the current best evaluation
  getBestEvaluation() {
    return this.currentAnalysis.bestEvaluation;
  }

  // Get all principal variations
  getPrincipalVariations() {
    return this.currentAnalysis.principalVariations;
  }

  // Get the analysis progress status
  isAnalysisInProgress() {
    return this.currentAnalysis.analysisInProgress;
  }

  // Stop the current analysis
  stopAnalysis() {
    if (!this.worker || !this.currentAnalysis.analysisInProgress) {
      return;
    }

    this.sendCommand('stop');
    this.currentAnalysis.analysisInProgress = false;

    // Remove the evaluation listener if it exists
    if (this.currentAnalysis.evaluationListener) {
      this.currentAnalysis.evaluationListener();
      this.currentAnalysis.evaluationListener = null;
    }
  }

  // Get the best move directly from a position
  getBestMove(fen, options = {}) {
    const { depth = 10 } = options;

    return new Promise((resolve, reject) => {
      if (!this.worker) {
        reject(new Error('Engine not initialized'));
        return;
      }


      // Stop any ongoing analysis
      if (this.currentAnalysis.analysisInProgress) {
        this.stopAnalysis();
      }

      // Setup a one-time listener for best move
      const listener = this.onMessage((message) => {
        // When we get the bestmove message, resolve the promise
        if (message.startsWith('bestmove')) {
          // Parse the best move from the engine output
          // Example format: "bestmove e2e4 ponder d7d5"
          const match = message.match(/bestmove\s+(\S+)(?:\s+ponder\s+(\S+))?/);

          if (match) {
            const bestMoveLAN = match[1]; // Long Algebraic Notation (e2e4)
            const ponderMove = match[2]; // Might be undefined


            // Cleanup the listener
            listener();

            // Need to convert LAN to SAN format (e2e4 to e4)
            try {
              // Create a temporary Chess.js object if available in window
              if (typeof window !== 'undefined' && window.Chess) {
                const tempChess = new window.Chess(fen);
                const move = tempChess.move({
                  from: bestMoveLAN.substring(0, 2),
                  to: bestMoveLAN.substring(2, 4),
                  promotion: bestMoveLAN.length > 4 ? bestMoveLAN[4] : undefined
                });

                resolve({
                  bestMove: {
                    lan: bestMoveLAN,
                    san: move ? move.san : bestMoveLAN,
                    from: bestMoveLAN.substring(0, 2),
                    to: bestMoveLAN.substring(2, 4),
                  },
                  ponder: ponderMove
                });
              } else {
                // If Chess.js not available, just return the LAN
                resolve({
                  bestMove: {
                    lan: bestMoveLAN,
                    san: bestMoveLAN, // No conversion possible
                    from: bestMoveLAN.substring(0, 2),
                    to: bestMoveLAN.substring(2, 4),
                  },
                  ponder: ponderMove
                });
              }
            } catch (error) {
              // Return the raw move if conversion fails
              resolve({
                bestMove: bestMoveLAN,
                ponder: ponderMove
              });
            }
          } else {
            // Couldn't parse the best move
            reject(new Error('Failed to parse best move from engine output'));
          }
        }
        // If the engine gives an error, reject the promise
        else if (message.includes('ERROR') || message.includes('error')) {
          listener(); // Clean up listener
          reject(new Error(`Engine error: ${message}`));
        }
      });

      // Set up a timeout in case the engine doesn't respond
      const timeout = setTimeout(() => {
        listener(); // Clean up listener
        reject(new Error('Engine timed out finding best move'));
      }, 5000); // 5 second timeout

      // Make sure engine is ready and then send commands
      this.waitForReady()
        .then(() => {
          this.sendCommand(`position fen ${fen}`);
          this.sendCommand(`go depth ${depth}`);
        })
        .catch(error => {
          listener(); // Clean up listener
          clearTimeout(timeout);
          reject(error);
        });
    });
  }

  // Register a callback for engine messages
  onMessage(callback) {
    this.callbacks.message.push(callback);

    // Return a function to remove this callback
    return () => {
      this.callbacks.message = this.callbacks.message.filter(cb => cb !== callback);
    };
  }

  // Clean up resources
  destroy() {
    try {
      if (this.worker) {
        this.stopAnalysis();
        this.sendCommand('quit');
        this.worker.terminate();
        this.worker = null;
      }
      this.isReady = false;
      if (this.currentAnalysis) {
        this.currentAnalysis.analysisInProgress = false;
      }
    } catch (error) {
      console.error('Error destroying Stockfish engine:', error);
    }
  }
}

// Create a singleton instance
const stockfishEngine = new StockfishEngine();

export default stockfishEngine; 